import { useAuth } from '@agentnet/auth';
import { Box, IconButton, Typography } from '@material-ui/core';
import { GetFileInfoProps, getIdvDetailedJson } from 'api/file/file-api';
import { IdentityVerificationResult, Party } from 'api/file/interfaces/get-file-order-insights';
import { getAdhocOrderJson } from 'api/orderInsights/order-insights-api';
import clsx from 'clsx';
import { ASC_STAFF } from 'utilities/constants/activity-rights';
import { DRAWER_CONTACT_VALIDATION_MESSAGE, DRAWER_TRANSACTION_MESSAGE } from 'features/help/constants';
import { getUnderwriterAlert } from 'features/services/identityVerification/underwriterAlertMessage';
import { ProfileContext, ProfileContextInterface } from 'hooks/ProfileContext';
import { IMarketPlace } from 'hooks/useMarketPlace';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import AgentNetButton from 'ui-kit/components/button/AgentNetButton';
import { AgentNet_Admin, FRAUD_VALIDATION, FRAUD_VALIDATION_READ_ONLY } from 'utilities/constants/activity-rights';
import { isPastExpiry } from 'utilities/dateFormatter';
import { TransactionStatusEnum } from 'utilities/verification/types';
import DisabledProducts from './DisabledProducts';
import IdvDetailedJsonDialog from './IdvDetailedJsonDialog';
import useStyles from './ListStyles';
import OrderInsightAlert from './OrderInsightAlerts';
import OrderInsightsRiskIcon from './OrderInsightsRiskIcon';
import VerifyPhoneNumber from './VerifyPhoneNumber';

interface IValidation {
  qaPrefix: string;
  title: string;
  description?: string;
  type?: 'ContactValidation' | 'EnhancedIdentityVerification';
  party: Party;
  isEnabled: boolean;
  productCode?: string;
  hasRisks: boolean;
  index: number;
  updatedPartyInfo: (updatedFields?: Partial<Party>) => void;
  isDrawer?: boolean;
  marketPlace: IMarketPlace;
  validateFileErrors?: (currentParty: Party) => Promise<boolean>;
  showValidation?: (show: boolean) => void;
  isPartyVerification?: boolean;
  isNonFaUnderwriter?: boolean;
  alwaysShowForm?: boolean;
}

const Validation = ({
  party,
  qaPrefix,
  title,
  description = '',
  type = 'ContactValidation',
  isEnabled,
  productCode = '',
  hasRisks,
  index,
  updatedPartyInfo,
  isDrawer = false,
  marketPlace,
  validateFileErrors,
  showValidation,
  isPartyVerification = false,
  isNonFaUnderwriter = false,
  alwaysShowForm = false,
}: IValidation) => {
  const classes = useStyles();
  const profileCtx: ProfileContextInterface = useContext(ProfileContext) ?? {};
  const { userFirm, profile } = profileCtx;
  const { fileId = '' } = useParams<GetFileInfoProps>();
  const history = useHistory();
  const { getAccessToken } = useAuth();
  const {
    doValidateContactInfo,
    validateContactInfoStatus,
    validateContactInfoStatusResults,
    enhancedIdentityVerification,
    enhancedIdentityVerificationStatusResults,
    enhancedIdentityVerificationStatus,
    cancelPartyVerification,
    cancelPartyVerificationStatus,
    fetchPartyVerificationList,
  } = marketPlace;

  const hasFraudValidation = useMemo(() => {
    return profile?.activityRights?.some((rights) => rights.ActivityRightId === FRAUD_VALIDATION);
  }, [profile?.activityRights]);

  const [persistedIdentityResults, setPersistedIdentityResults] = useState<IdentityVerificationResult | null>(
    party.enhancedIdentifyVerification || null,
  );

  const manualUpdateRef = useRef(false);

  const hasReadOnlyFraudValidation = useMemo(() => {
    return profile?.activityRights?.some((rights) => rights.ActivityRightId === FRAUD_VALIDATION_READ_ONLY);
  }, [profile?.activityRights]);

  const isAgentNetAdmin = useMemo(() => {
    return profile?.activityRights?.some((rights) => rights.ActivityRightId === AgentNet_Admin);
  }, [profile?.activityRights]);

  // When the product is off for the selected underwriter, the form is shown read-only behind the alert.
  const underwriterAlert = useMemo(() => {
    if (!isEnabled && !alwaysShowForm && !isNonFaUnderwriter) return null;

    const alert = getUnderwriterAlert({
      productName: title,
      isEnabled,
      isNonFaUnderwriter,
      isAgentNetAdmin: !!isAgentNetAdmin,
    });

    if (!alert) return null;

    const enableAction = (
      <IconButton
        color="inherit"
        size="small"
        onClick={() => history.push(`/admin/integrations-features?productCode=${productCode}`)}
        data-testid={`${qaPrefix}-button-enable-underwriter${isDrawer ? '' : `-${index}`}`}
      >
        <Typography className={classes.alertActionLabel}>Enable</Typography>
      </IconButton>
    );

    return (
      <OrderInsightAlert
        className={classes.underwriterAlert}
        alertWithContent={[
          {
            severity: 'warning',
            content: alert.message,
            ...(alert.showEnableAction ? { action: enableAction } : {}),
          },
        ]}
      />
    );
  }, [isNonFaUnderwriter, isEnabled, alwaysShowForm, title, isAgentNetAdmin, productCode, qaPrefix, isDrawer, index]);

  const shouldSyncPhoneNumber = () => {
    const contactInfo = party.contactValidation || validateContactInfoStatusResults;
    const nonAdmin = !hasFraudValidation && !hasReadOnlyFraudValidation;

    const isContactValidationRendered = !contactInfo && !nonAdmin;

    const isIdentityResultStatusCompleted =
      persistedIdentityResults?.transactionStatus === TransactionStatusEnum.Completed;
    const isIdentityResultStatusPending = persistedIdentityResults?.transactionStatus === TransactionStatusEnum.Pending;
    let isIdentityVerificationRendered = false;

    if (isIdentityResultStatusCompleted) {
      const { summary } = persistedIdentityResults;
      const { transactionDate } = summary ?? {};
      const isOlderThan90Days = isPastExpiry(transactionDate);
      isIdentityVerificationRendered = isOlderThan90Days;
    }
    if (!isIdentityVerificationRendered && !nonAdmin && !isIdentityResultStatusPending) {
      isIdentityVerificationRendered = true;
    }

    return isContactValidationRendered && isIdentityVerificationRendered;
  };

  const isEnhancedIdentityVerification = type === 'EnhancedIdentityVerification';
  const isIdentityCompleted = persistedIdentityResults?.transactionStatus === TransactionStatusEnum.Completed;
  const transactionDate = persistedIdentityResults?.summary?.transactionDate;
  const isReportAvailable = isIdentityCompleted && transactionDate && !isPastExpiry(transactionDate);
  const transactionId = persistedIdentityResults?.transactionId ?? '';
  const resolvedFileId =
    fileId ||
    party?.fileId ||
    validateContactInfoStatusResults?.fileId ||
    enhancedIdentityVerificationStatusResults?.fileId ||
    '';
  const showJsonResponseButton =
    isDrawer &&
    isEnhancedIdentityVerification &&
    profile?.activityRights?.some((rights) => rights.ActivityRightId === ASC_STAFF) &&
    isReportAvailable &&
    !!transactionId &&
    !!resolvedFileId;

  const [jsonDialogOpen, setJsonDialogOpen] = useState(false);
  const [jsonData, setJsonData] = useState<unknown>(null);
  const [jsonLoading, setJsonLoading] = useState(false);
  const [jsonError, setJsonError] = useState<string | null>(null);

  const handleViewDetailedJson = useCallback(async () => {
    if (!resolvedFileId || !transactionId) return;
    setJsonLoading(true);
    setJsonError(null);
    setJsonDialogOpen(true);
    try {
      const token = await getAccessToken();
      const data = isPartyVerification
        ? await getAdhocOrderJson(transactionId, resolvedFileId, token)
        : await getIdvDetailedJson(resolvedFileId, transactionId, token);
      setJsonData(data);
    } catch {
      setJsonError('Failed to load detailed JSON data. Please try again.');
    } finally {
      setJsonLoading(false);
    }
  }, [resolvedFileId, transactionId, getAccessToken, isPartyVerification]);

  const handleCloseJsonDialog = useCallback(() => {
    setJsonDialogOpen(false);
    setJsonData(null);
    setJsonError(null);
  }, []);

  const riskIcon = useMemo(() => {
    const risksCount = validateContactInfoStatusResults?.risks?.length;
    const risks = hasRisks || risksCount;

    if (!isEnabled || !(hasFraudValidation || hasReadOnlyFraudValidation)) return 'Instruction';

    if (
      (type === 'ContactValidation' && (party?.contactValidation || validateContactInfoStatusResults)) ||
      (type === 'EnhancedIdentityVerification' && (party?.enhancedIdentifyVerification || persistedIdentityResults))
    ) {
      const isPartyTransactionPending = [
        TransactionStatusEnum.Pending,
        TransactionStatusEnum.AwaitingUserAction,
      ].includes(party.enhancedIdentifyVerification?.transactionStatus ?? ('' as TransactionStatusEnum));

      const isEnhancedIdentityVerificationTransactionPending = [
        TransactionStatusEnum.Pending,
        TransactionStatusEnum.AwaitingUserAction,
      ].includes(persistedIdentityResults?.transactionStatus ?? ('' as TransactionStatusEnum));

      if (
        type === 'EnhancedIdentityVerification' &&
        (isPartyTransactionPending || isEnhancedIdentityVerificationTransactionPending)
      )
        return 'Pending';

      if (
        type === 'EnhancedIdentityVerification' &&
        (party.enhancedIdentifyVerification?.transactionStatus === TransactionStatusEnum.Failed ||
          persistedIdentityResults?.transactionStatus === TransactionStatusEnum.Failed)
      )
        return 'Error';

      if (
        (type === 'ContactValidation' && (party?.contactValidation || validateContactInfoStatusResults)) ||
        (type === 'EnhancedIdentityVerification' && party?.enhancedIdentifyVerification)
      )
        return risks ? 'Alert' : 'Success';

      if (!risks) return 'Loading';
    } else {
      return 'Loading';
    }
  }, [
    type,
    isEnabled,
    party,
    validateContactInfoStatus,
    hasFraudValidation,
    hasReadOnlyFraudValidation,
    validateContactInfoStatusResults,
    persistedIdentityResults,
  ]);

  useEffect(() => {
    if (manualUpdateRef.current) {
      manualUpdateRef.current = false;
      return;
    }

    setPersistedIdentityResults(
      enhancedIdentityVerificationStatusResults || party.enhancedIdentifyVerification || null,
    );
  }, [enhancedIdentityVerificationStatusResults, party.enhancedIdentifyVerification]);

  const partyInfo = useMemo(
    () => ({
      firmId: userFirm?.firmId ?? '',
      fileId:
        fileId ||
        party?.fileId ||
        validateContactInfoStatusResults?.fileId ||
        enhancedIdentityVerificationStatusResults?.fileId ||
        '',
      partyId: party.id,
      partyType: party.partyType,
      party: party.filePartyType,
      isSpouse: party.isSpouse,
      address: party.address,
      firstName: party.firstName,
      lastName: party.lastName,
    }),
    [
      userFirm?.firmId,
      fileId,
      party?.fileId,
      validateContactInfoStatusResults?.fileId,
      enhancedIdentityVerificationStatusResults?.fileId,
    ],
  );

  const syncPartyPhoneOrEmail = (updatedFields?: Partial<Party>, hasFieldRendered?: boolean) => {
    const isFieldsRendered: boolean = shouldSyncPhoneNumber();
    if (hasFieldRendered) return isFieldsRendered;
    if (isFieldsRendered) {
      updatedPartyInfo(updatedFields);
    }
  };

  const updateTransactionStatus = (updatedFields: { transactionStatus: TransactionStatusEnum }) => {
    manualUpdateRef.current = true;

    if (party?.enhancedIdentifyVerification) {
      updatedPartyInfo({
        enhancedIdentifyVerification: {
          ...party.enhancedIdentifyVerification,
          ...updatedFields,
          attempt:
            party.enhancedIdentifyVerification?.transactionStatus === TransactionStatusEnum.AwaitingUserAction
              ? (party.enhancedIdentifyVerification?.attempt ?? 1) - 1
              : party.enhancedIdentifyVerification?.attempt ?? 0,
        },
      });
    }

    setPersistedIdentityResults((prevState: IdentityVerificationResult | null) => {
      if (prevState?.submittedData) {
        return {
          ...prevState,
          transactionStatus: updatedFields.transactionStatus ?? prevState.transactionStatus,
          attempt:
            party.enhancedIdentifyVerification?.transactionStatus === TransactionStatusEnum.AwaitingUserAction
              ? (party.enhancedIdentifyVerification?.attempt ?? 1) - 1
              : party.enhancedIdentifyVerification?.attempt ?? 0,
        };
      }
      return prevState;
    });
  };

  return (
    <div className={!isDrawer ? classes.brdBtm : undefined}>
      <div className={clsx(classes.flex, isDrawer && classes.drawerContainer)}>
        {isDrawer ? (
          <div className={classes.drawerHeadingContainer}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h4" className={clsx(classes.drawerHeading)}>
                {title}
              </Typography>
              {showJsonResponseButton && (
                <AgentNetButton
                  variant="outlined"
                  size="small"
                  color="primary"
                  style={{ marginRight: 16 }}
                  data-testid={`${qaPrefix}-button-json-response`}
                  onClick={handleViewDetailedJson}
                  disabled={!transactionId}
                >
                  JSON Response
                </AgentNetButton>
              )}
            </Box>
            <Typography
              variant="h6"
              className={clsx(
                classes.marginBottom,
                classes.marginTopSmall,
                classes.transactionMessageContainer,
                classes.smallText,
              )}
            >
              {type === 'ContactValidation' ? DRAWER_CONTACT_VALIDATION_MESSAGE : DRAWER_TRANSACTION_MESSAGE}
            </Typography>
          </div>
        ) : (
          <div className={classes.labelCol}>
            <span className={classes.flexCenter}>
              <OrderInsightsRiskIcon riskSeverity={riskIcon} />
              <Typography
                variant="h4"
                color="textSecondary"
                className={clsx(classes.marginLeft, classes.typographyFontSize)}
              >
                {title}:
              </Typography>
            </span>
          </div>
        )}
        <div className={clsx(classes.flexGrow, isDrawer && classes.drawerContent)}>
          {isEnabled || isNonFaUnderwriter || alwaysShowForm ? (
            <VerifyPhoneNumber
              hasReadOnlyFraudValidation={hasReadOnlyFraudValidation}
              hasFraudValidation={hasFraudValidation}
              party={party}
              type={type}
              index={index}
              identityResults={persistedIdentityResults}
              description={description}
              qaPrefix={qaPrefix}
              partyInfo={partyInfo}
              syncPartyPhoneOrEmail={syncPartyPhoneOrEmail}
              updatedPartyInfo={updatedPartyInfo}
              isDrawer={isDrawer}
              validateFileErrors={validateFileErrors}
              validationChecks={{
                doValidateContactInfo,
                validateContactInfoStatusResults,
                enhancedIdentityVerification,
                enhancedIdentityVerificationStatus,
                updateTransactionStatus,
                cancelPartyVerification,
                cancelPartyVerificationStatus,
              }}
              showValidation={showValidation}
              isPartyVerification={isPartyVerification}
              isFieldsDisabled={!isEnabled}
              underwriterAlert={underwriterAlert}
            />
          ) : (
            <DisabledProducts
              qaPrefix={qaPrefix}
              index={index}
              type={type}
              description={description}
              productCode={productCode}
              isAgentNetAdmin={isAgentNetAdmin}
              contactInfo={party.contactValidation || validateContactInfoStatusResults}
              fileId={partyInfo.fileId}
              identityResults={persistedIdentityResults}
            />
          )}
        </div>
      </div>
      {showJsonResponseButton && (
        <IdvDetailedJsonDialog
          open={jsonDialogOpen}
          onClose={handleCloseJsonDialog}
          jsonData={jsonData}
          loading={jsonLoading}
          error={jsonError}
          customerName={persistedIdentityResults?.summary?.customerName}
        />
      )}
    </div>
  );
};

export default Validation;
